/**
 * SettingsPage (B1.18) — Business operator settings shell.
 *
 * Authority: UX Blueprint §4 Navigation + §9 UX Invariants.
 *
 * Layout (>=1024px / iPad landscape):
 *   ┌──────────────┬────────────────────────────────┐
 *   │ Vertical Tabs│           Tab Content          │
 *   │ (280px)      │                                │
 *   └──────────────┴────────────────────────────────┘
 *
 * Layout (<1024px / iPad portrait):
 *   Horizontal scrolling tab strip at top, full-width content below.
 *
 * URL state:
 *   /settings            → redirects to /settings/profile
 *   /settings/:tab       → controls `value` on Radix Tabs
 *
 * Why this is a Claude-owned component:
 *   - URL ↔ tab state sync is wiring logic, not visual polish
 *   - Tab content is form-heavy; a Lovable regenerate would wipe field wiring
 *
 * When the B1.14 BusinessProfileEditor (Faz 0.5) lands it will slot in as
 * an additional tab here via the `SETTINGS_TABS` registry.
 */
import { Bell, Clock, IdentificationCard, ShieldCheck } from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../shared/ui/Tabs';
import { cn } from '../../shared/utils/cn';
import { HoursTab } from './tabs/HoursTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { ProfileTab } from './tabs/ProfileTab';
import { SecurityTab } from './tabs/SecurityTab';

type TabId = 'profile' | 'hours' | 'notifications' | 'security';

interface TabConfig {
  id: TabId;
  labelKey: string;
  descriptionKey: string;
  icon: ReactNode;
  Panel: () => JSX.Element;
}

const SETTINGS_TABS: TabConfig[] = [
  {
    id: 'profile',
    labelKey: 'tabs.profile',
    descriptionKey: 'tabs.profileDescription',
    icon: <IdentificationCard size={22} weight="regular" />,
    Panel: ProfileTab,
  },
  {
    id: 'hours',
    labelKey: 'tabs.hours',
    descriptionKey: 'tabs.hoursDescription',
    icon: <Clock size={22} weight="regular" />,
    Panel: HoursTab,
  },
  {
    id: 'notifications',
    labelKey: 'tabs.notifications',
    descriptionKey: 'tabs.notificationsDescription',
    icon: <Bell size={22} weight="regular" />,
    Panel: NotificationsTab,
  },
  {
    id: 'security',
    labelKey: 'tabs.security',
    descriptionKey: 'tabs.securityDescription',
    icon: <ShieldCheck size={22} weight="regular" />,
    Panel: SecurityTab,
  },
];

const VALID_TABS = new Set<TabId>(SETTINGS_TABS.map((t) => t.id));

function isValidTab(value: string | undefined): value is TabId {
  return typeof value === 'string' && VALID_TABS.has(value as TabId);
}

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const params = useParams<{ tab?: string }>();

  if (!params.tab) {
    return <Navigate to="/settings/profile" replace />;
  }
  if (!isValidTab(params.tab)) {
    return <Navigate to="/settings/profile" replace />;
  }

  const activeTab: TabId = params.tab;

  const handleChange = (next: string) => {
    if (isValidTab(next)) {
      navigate(`/settings/${next}`, { replace: false });
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <header className="mb-biz-section">
        <h1 className="text-biz-3xl biz-heading font-semibold mb-biz-content">
          {t('pageTitle')}
        </h1>
        <p className="text-biz-base text-biz-muted-foreground">
          {t('pageSubtitle')}
        </p>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={handleChange}
        orientation="vertical"
        className="flex flex-col lg:flex-row gap-biz-section"
      >
        <div className="lg:w-[280px] shrink-0">
          <TabsList
            orientation="vertical"
            aria-label={t('pageTitle')}
            className={cn(
              'hidden lg:flex',
              'sticky top-biz-section',
            )}
          >
            {SETTINGS_TABS.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                orientation="vertical"
                icon={tab.icon}
                description={t(tab.descriptionKey)}
              >
                {t(tab.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsList
            orientation="horizontal"
            aria-label={t('pageTitle')}
            className={cn(
              'lg:hidden',
              'overflow-x-auto -mx-biz-card-lg px-biz-card-lg',
              'flex-nowrap whitespace-nowrap',
            )}
          >
            {SETTINGS_TABS.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                orientation="horizontal"
                icon={tab.icon}
              >
                {t(tab.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 min-w-0">
          {SETTINGS_TABS.map((tab) => (
            <TabsContent key={tab.id} value={tab.id}>
              <tab.Panel />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
